import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  FunctionComponent,
  HTMLAttributes,
} from 'react';
import _, {
  isEmpty,
  isFinite,
  map,
  isNil,
  isFunction,
  head,
  get,
} from 'lodash';
import { defaultRenderStrategy } from './chart-tooltip.util';
import { ChartTooltipContext, TOOLTIP_ID } from './chart-tooltip.hook';
import classnames from 'classnames';
import { useReportLinkEnabled } from '../base-cartesian-chart';
import { IViz } from '../../interfaces';
import {
  useOpenVizSelector,
  useVizOptionSelector,
} from '../../../common/redux/selectors/viz-selector.hook';
import { ChartTooltipReportLinkNote } from './ChartToolTip';
import { KeyValueTooltipContentArg } from './chart-tooltip.interface';
import { shortid } from '../../../common/utilities/shortid-adapter';
import { VIZ_OPTION_IDS } from '../../../common';
import { i18nUtils, messages } from '../../../i18n';
import { Viz } from '../../VizUtil';
import { useFiscalCalendarInfo } from '../../../common/utilities/fiscal-info.hook';

/**
 * makeFinite is a utility that fixes some bugs
 * @param num
 */
const makeFinite = num => (isFinite(num) ? num : 0);

/**
 * useKeyValueTooltipContentPattern is a utility for the common pattern for tooltips, listing rows of preformatted labels/values
 */
export const useKeyValueTooltipContentPattern = (
  rows: KeyValueTooltipContentArg[],
) => {
  const viz = useOpenVizSelector();
  const { useFiscalCalendar } = useFiscalCalendarInfo({ discoveryId: viz?.id });
  const fieldDictionary = useMemo(() => {
    const allFields = Viz.getAllAvailableFields(viz);
    return Viz.createFieldDictionary('name', allFields);
  }, [viz]);

  return map(rows, ({ name = '', value = '' }) => {
    const label = i18nUtils.translateFieldName(
      messages,
      get(fieldDictionary, name, name),
      useFiscalCalendar,
    );

    return (
      <div className='tooltip-entry'>
        <span className='chart-tooltip-label'>{label}</span>
        <span className='chart-tooltip-value'>{value}</span>
      </div>
    );
  });
};

/**
 * ChartTooltip (the functional component) is heavily derived from the class-based ChartTooltip.
 * It is not a fully-functional replacement - just enough to refactor the unnecessary complexity out.
 * @param props
 * @constructor
 */
export const ChartTooltip: FunctionComponent<HTMLAttributes<SVGGElement>> = ({
  className,
}) => {
  const {
    vizId,
    container = null,
    anchor: { posX: suggestedAnchorX = 0, posY: suggestedAnchorY = 0 } = {},
    tooltipData,
  } = useContext(ChartTooltipContext);
  const isAdditionalWaterfallBar = [
    messages.chartSpecs.waterfall.missing,
    messages.chartSpecs.waterfall.extra,
  ].includes(head<{ name?: string }>(tooltipData)?.name);

  const tooltipInnerContentRef = useRef<HTMLDivElement>(null);
  const tooltipOuterContentRef = useRef<SVGGElement>(null); // used for d3 to mouse events
  const [tooltipKey, setTooltipKey] = useState<string>('tooltip');
  const [showTooltip, setShowTooltip] = useState(false);

  const arrowHeight = 8;
  const tooltipArrowWidth = 6;
  const tooltipTextOuterPadding = 8;
  const tooltipPaddingX = 26;
  const tooltipRectRadius = 2;

  useEffect(() => {
    setTooltipKey(`tooltip-${shortid.generate()}`);
  }, []);

  const { enabled: isDrillLinkingEnabled } = useReportLinkEnabled(vizId);
  const linkToReport: IViz | object = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.linkToReport,
    defaultValue: {},
  });

  useEffect(() => {
    // eventually will have timeout based on inactivity here
    if (!isNil(tooltipData) && !isEmpty(tooltipData)) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
    }
  }, [setShowTooltip, tooltipData]);

  const tooltipContent = useKeyValueTooltipContentPattern(tooltipData);

  const { width: mousableAreaWidth = 0, height: mousableAreaHeight = 0 } =
    isFunction(container?.getBoundingClientRect)
      ? container?.getBoundingClientRect()
      : {};

  const { current: _tooltipContentRef } = tooltipInnerContentRef ?? {};
  const { innerHTML: tooltipHTML = '' } = _tooltipContentRef ?? {};
  const hasContent = !isEmpty(tooltipHTML);

  const {
    width: tooltipInnerContentWidth = 0,
    height: tooltipInnerContentHeight = 0,
  } = useMemo<Partial<DOMRect>>(() => {
    if (showTooltip && hasContent) {
      // recalculate rect based on content
      return _tooltipContentRef?.getBoundingClientRect() ?? {};
    }
    return {};
  }, [_tooltipContentRef, hasContent, showTooltip]);

  const { tooltipRectHeight, tooltipRectWidth, tooltipWidth } = useMemo(() => {
    const tooltipRectWidth = makeFinite(
      tooltipInnerContentWidth + tooltipTextOuterPadding * 2,
    );
    const tooltipRectHeight = makeFinite(
      tooltipInnerContentHeight + tooltipTextOuterPadding * 2,
    );
    const tooltipWidth = makeFinite(
      tooltipRectWidth + tooltipTextOuterPadding * 2,
    );
    return {
      tooltipRectHeight,
      tooltipRectWidth,
      tooltipWidth,
    };
  }, [
    tooltipInnerContentWidth,
    tooltipInnerContentHeight,
    tooltipTextOuterPadding,
  ]);

  const {
    xPos,
    yPos,
    arrow: arrowDirection,
    arrowYOffset,
    downArrowXOffset,
    upArrowXOffset,
  } = useMemo(() => {
    return defaultRenderStrategy({
      width: mousableAreaWidth,
      height: mousableAreaHeight,
      x: suggestedAnchorX,
      y: suggestedAnchorY,
      arrowHeight,
      tooltipWidth,
      tooltipRectWidth,
      tooltipRectHeight,
      tooltipPaddingX,
      tooltipTextOuterPadding,
    });
  }, [
    mousableAreaWidth,
    mousableAreaHeight,
    suggestedAnchorX,
    suggestedAnchorY,
    arrowHeight,
    tooltipWidth,
    tooltipRectWidth,
    tooltipRectHeight,
    tooltipPaddingX,
    tooltipTextOuterPadding,
  ]);

  // remove from DOM if not visible
  if (!showTooltip) {
    return null;
  }

  return (
    <g id={TOOLTIP_ID}>
      <g
        key={tooltipKey}
        style={{ pointerEvents: 'none' }}
        ref={tooltipOuterContentRef}
        className={`chart-tooltip-container${hasContent ? '' : ' invisible'}`}
        transform={`translate(${xPos}, ${yPos})`}
      >
        <rect
          style={{ visibility: 'hidden' }}
          width={tooltipRectWidth}
          height={tooltipRectHeight}
        />
        <path
          className={`leftArrow ${
            arrowDirection === 'left' ? 'visible' : 'hidden'
          }`}
          d='M0 4 L6 0 L6 8 Z'
          transform={`translate(0, ${arrowYOffset})`}
        />
        <path
          className={`rightArrow ${
            arrowDirection === 'right' ? 'visible' : 'hidden'
          }`}
          d='M6 4 L0 0 L0 8 Z'
          transform={`translate(${
            tooltipRectWidth + tooltipArrowWidth
          }, ${arrowYOffset})`}
        />
        <path
          className={`downArrow ${
            arrowDirection === 'down' ? 'visible' : 'hidden'
          }`}
          d='M0 0 L4 6 L8 0 Z'
          transform={`translate(${
            tooltipRectWidth / 2 + tooltipArrowWidth / 2 + downArrowXOffset
          }, ${tooltipRectHeight})`}
        />
        <path
          className={`upArrow ${
            arrowDirection === 'up' ? 'visible' : 'hidden'
          }`}
          d='M0 8 L4 2 L8 8 Z'
          transform={`translate(${
            tooltipRectWidth / 2 + tooltipArrowWidth / 2 + upArrowXOffset
          }, ${0 - arrowHeight})`}
        />
        <rect
          className='tooltipRect'
          width={tooltipRectWidth}
          height={tooltipRectHeight}
          rx={tooltipRectRadius}
          ry={tooltipRectRadius}
          x={tooltipArrowWidth}
          y='0'
        />
        {/* Safari won't render a foreign object without height & width properties set */}
        <foreignObject width={tooltipRectWidth} height={tooltipRectHeight}>
          <div
            className={classnames(className, 'tooltip-content')}
            ref={tooltipInnerContentRef}
            style={{ paddingLeft: `${tooltipArrowWidth}px` }}
          >
            {tooltipContent}
            {isDrillLinkingEnabled && !isAdditionalWaterfallBar && (
              <ChartTooltipReportLinkNote name={(linkToReport as IViz)?.name} />
            )}
          </div>
        </foreignObject>
      </g>
    </g>
  );
};
